import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { PlusCircle, Trash, PencilSquare, Floppy, XLg } from 'react-bootstrap-icons'
import InputNumber from '../components/form/InputNumber'
import InputDate from '../components/form/InputDate'

import PaymentsPartnersService from '../services/PaymentsPartnersService'
import ProjectsService from '../services/ProjectsService'
import PartnersService from '../services/PartnersService'

import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"
import formatCurrency from "../helpers/currencyHelper"

const PaymentsPartnersExecutedForm = (props) => {
    
    const intl = useIntl()
    const [elementId, setElementId] = useState(props.elementId)
    const [project, setProject] = useState()
    const [partner, setPartner] = useState()
    const [paymentPartner, setPaymentPartner] = useState()
    const [newPayment, setNewPayment] = useState({
        date: formatDate(Date.now(), 'YYYY-MM-DD'),
        concept: '',
        amount: '',
    })
    const [payments, setPayments] = useState([])
    const [totalPaid, setTotalPaid] = useState(0)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)
    const [editingIndex, setEditingIndex] = useState(null)
    const [editedPayment, setEditedPayment] = useState(null)

    // Fetch project
    const fetchProject = async (projectId) => {
        try {
            const response = await ProjectsService.getProject(projectId)
            setProject(response.data)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch partner
    const fetchPartner = async (partnerId) => {
        try {
            const response = await PartnersService.getPartner(partnerId)
            setPartner(response.data)
        } catch (err) {
            setError(err)
        }
    }

    const getData = async () => {
        await PaymentsPartnersService.getPaymentPartner(elementId).then(
            response => {
                setPaymentPartner(response.data || {})
                setPayments(response.data.payments || [])
                fetchProject(response.data.projectId)
                fetchPartner(response.data.partnerId)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {
        if (elementId) {
            getData()
        }
    }, [elementId])

    useEffect(() => {
        if (project && partner) {
            setIsLoaded(true)
        }
    }, [project, partner])

    useEffect(() => {
        if (isLoaded) {
            handleUpdatePaymentsPartner()
        }
        const totalPaidValue = payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
        setTotalPaid(totalPaidValue)
    }, [payments])


    const handleInputChange = (e) => {
        const { name, value } = e.target
        if (name === "amount") {
            setNewPayment({ ...newPayment, [name]: parseFloat(value) });
        } else {
            setNewPayment({ ...newPayment, [name]: value });
        }
    }

    const handleAddPayment = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        const form = e.currentTarget
        if (!form.checkValidity()) {
            setFormValid(true)
            return
        }

        if (newPayment.date && newPayment.concept && newPayment.amount > 0) {
            setPayments([...payments, { ...newPayment }])
            setNewPayment({ date: formatDate(Date.now(), 'YYYY-MM-DD'), concept: '', amount: '' })
        }
        setFormValid(false)
    }

    const handleRemovePayment = (index) => {
        const confirmRemove = window.confirm(intl.formatMessage({ id: "paymentsPartnersExecutedConfirmDelete", defaultMessage: "Are you sure you want to delete this payment?" }));
        if (confirmRemove) {
            const updatedPayments = payments.filter((_, i) => i !== index)
            setPayments(updatedPayments)
        }
    }

    const handleEditPayment = (index) => {
        setEditingIndex(index)
        setEditedPayment({ 
            ...payments[index], 
            date: formatDate(payments[index].date, 'YYYY-MM-DD')
        
        })
    }

    const handleEditInputChange = (e) => {
        const { name, value } = e.target
        setEditedPayment({ ...editedPayment, [name]: value })
    }

    const handleSaveEdit = () => {
        const updatedPayments = [...payments]
        updatedPayments[editingIndex] = {
            ...editedPayment,
            amount: parseFloat(editedPayment.amount) || 0 // Asegurar que amount sea número
        }
        setPayments(updatedPayments);
        setEditingIndex(null);
    }

    const handleUpdatePaymentsPartner = async () => {
        const paymentsPartner = {
            payments,
        }

        try {
            await PaymentsPartnersService.updatePaymentPartner(paymentsPartner, elementId)
        } catch (error) {
            setShowMsg({ text: error || "An error occurred", style: 'danger' })
            setShowAlert(true)
        }
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleAddPayment}>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="projectId">
                            <Form.Label>
                                <FormattedMessage id="paymentsProject" defaultMessage="Project" />:
                            </Form.Label>
                            <strong className="ms-2">{project.name}</strong>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="partnerId">
                            <Form.Label>
                                <FormattedMessage id="paymentsPartner" defaultMessage="Partner" />:
                            </Form.Label>
                            <strong className="ms-2">{partner.name}</strong>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="paymentPartnerAmount">
                            <Form.Label>
                                <FormattedMessage id="paymentsAmount" defaultMessage="Grant amount" />:
                            </Form.Label>
                            <strong className="ms-2">{formatCurrency(paymentPartner.amount)}</strong>
                        </Form.Group>
                    </Col>
                </Row>

                <div>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th><small><FormattedMessage id="paymentsPaymentDate" defaultMessage="Payment date" /></small></th>
                                <th><small><FormattedMessage id="paymentsConcept" defaultMessage="Concept" /></small></th>
                                <th><small><FormattedMessage id="paymentsPaymentAmount" defaultMessage="Payment amount" /></small></th>
                                <th><small><FormattedMessage id="paymentsPaymentBalance" defaultMessage="Balance" /></small></th>
                                <th><small><FormattedMessage id="textActions" defaultMessage="Actions" /></small></th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((payment, index) => {
                                const totalPaid = payments.slice(0, index + 1).reduce((sum, p) => sum + p.amount, 0);
                                const remainingBalance = paymentPartner.amount - totalPaid;

                                return (
                                    <tr key={index}>
                                        {/* Fecha de pago */}
                                        <td>
                                            {editingIndex === index ? (
                                                <InputDate
                                                    fieldName="date"
                                                    value={editedPayment.date}
                                                    onChange={handleEditInputChange}
                                                    size="sm"
                                                />
                                            ) : (
                                                <small>{formatDate(payment.date, 'DD-MM-YYYY')}</small>
                                            )}
                                        </td>

                                        {/* Concepto */}
                                        <td>
                                            {editingIndex === index ? (
                                                <Form.Control
                                                    type="text"
                                                    name="concept"
                                                    value={editedPayment.concept}
                                                    onChange={handleEditInputChange}
                                                    size="sm"
                                                />
                                            ) : (
                                                <small>{payment.concept}</small>
                                            )}
                                        </td>

                                        {/* Importe pagado */}
                                        <td className="text-end">
                                            {editingIndex === index ? (
                                                <InputNumber
                                                    fieldName="amount"
                                                    value={editedPayment.amount}
                                                    onChange={handleEditInputChange}
                                                    size="sm"
                                                />
                                            ) : (
                                                <small>{formatCurrency(payment.amount)}</small>
                                            )}
                                        </td>

                                        {/* Saldo pendiente */}
                                        <td className="text-end">
                                            <small>{formatCurrency(remainingBalance)}</small>
                                        </td>

                                        {/* Acciones */}
                                        <td className="text-center">
                                            {editingIndex === index ? (
                                                <>
                                                    <Button variant="success" size="sm" onClick={handleSaveEdit}><Floppy /></Button>
                                                    &nbsp;
                                                    <Button variant="secondary" size="sm" onClick={() => setEditingIndex(null)}><XLg /></Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button variant="primary" size="sm" onClick={() => handleEditPayment(index)}> <PencilSquare /></Button>
                                                    &nbsp;
                                                    <Button variant="danger" size="sm" onClick={() => handleRemovePayment(index)}> <Trash /></Button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {/* Nueva fila para agregar un pago */}
                            <tr>
                                <td>
                                    <Form.Group className="mb-3" controlId="paymentDate">
                                        <InputDate
                                            fieldName="date"
                                            value={newPayment.date}
                                            onChange={handleInputChange}
                                            required
                                            size="sm"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            <small><FormattedMessage id="paymentsPaymentDateInvalid" defaultMessage="Provide a payment date" /></small>
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </td>

                                <td>
                                    <Form.Group className="mb-3" controlId="paymentConcept">
                                        <Form.Control
                                            type="text"
                                            name="concept"
                                            value={newPayment.concept}
                                            onChange={handleInputChange}
                                            required
                                            size="sm"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            <small><FormattedMessage id="paymentsConceptInvalid" defaultMessage="Provide a concept" /></small>
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </td>

                                <td className="text-end">
                                    <Form.Group className="mb-3" controlId="paymentAmount">
                                        <InputNumber
                                            fieldName="amount"
                                            value={newPayment.amount}
                                            onChange={handleInputChange}
                                            required
                                            size={'sm'}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            <small><FormattedMessage id="paymentsPaymentAmountInvalid" defaultMessage="Provide a payment amount" /></small>
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </td>

                                <td colSpan={2} className="text-center">
                                    <Button type="submit" variant="primary" size="sm" onClick={handleAddPayment}>
                                        <PlusCircle /> <FormattedMessage id="paymentsAdd" defaultMessage="Add payment" />
                                    </Button>
                                </td>
                            </tr>
                        </tbody>

                        <tfoot>
                            <tr>
                                <td colSpan="2"><small><strong>Total</strong></small></td>
                                <td className="text-end"><b><small>{formatCurrency(totalPaid)}</small></b></td>
                                <td className="text-end"><small>{formatCurrency(paymentPartner.amount - totalPaid)}</small></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

            </Form>
        </div>
    )

}

export default PaymentsPartnersExecutedForm